/**
 * The hall, full screen, with the panels floating over it: a layout being edited, or one opened
 * through a view-only link (the same hall and views, nothing that changes the layout).
 */

import type { FileAccess, HallResponse } from '@mawkeb/shared/manifest';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { duplicate, remove, rotateBy, setLocked, STEP } from '../engine/editing';
import { layoutState, useLayout } from '../state/layout';
import { saving } from '../state/saving';
import { rememberView, useVenue, venue, type ViewMode } from '../state/store';
import { Inspector } from './Inspector';
import { Library, PlacingHint } from './Library';
import { CleanControls, Counts, Diagnostics, Loading, Settings, TapePanel, WalkHelp } from './Panels';
import { PlanPreview } from './PlanPreview';
import { Stage, viewer } from './Stage';
import { TopBar } from './TopBar';

export const HALL_ID = 'zahraa-hall';

function stamp(d = new Date()) {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}.${p(d.getMinutes())}.${p(d.getSeconds())}`;
}

/** Measures the floating panels so the plan view fits the hall into the space they leave. */
function useInsets(clean: boolean) {
  const set = useVenue((s) => s.set);
  const tool = useVenue((s) => s.tool);
  const loading = useVenue((s) => s.load.phase !== 'ready');
  const library = useVenue((s) => s.library);
  const selected = useLayout((s) => s.selection.length > 0);
  useEffect(() => {
    const measure = () => {
      const insets = { top: 16, bottom: 16, left: 16, right: 16 };
      if (!clean) {
        for (const el of document.querySelectorAll<HTMLElement>('.topbar, .loading, .counts, .side, .library, .phone-edit .tool')) {
          const r = el.getBoundingClientRect();
          if (!r.width) continue;
          const wide = r.width > innerWidth * 0.6;
          const tall = r.height > innerHeight * 0.5;
          if (!wide && tall && r.right < innerWidth / 2) insets.left = Math.max(insets.left, r.right + 12);
          else if (!wide && el.classList.contains('side') && r.left > innerWidth / 2) insets.right = Math.max(insets.right, innerWidth - r.left + 12);
          else if (r.top + r.height / 2 < innerHeight / 2) insets.top = Math.max(insets.top, r.bottom + 12);
          else insets.bottom = Math.max(insets.bottom, innerHeight - r.top + 12);
        }
      }
      const cur = venue().insets;
      if (Math.abs(cur.top - insets.top) + Math.abs(cur.bottom - insets.bottom) + Math.abs(cur.right - insets.right) + Math.abs(cur.left - insets.left) > 2) {
        set({ insets });
      }
    };
    const raf = requestAnimationFrame(measure);
    addEventListener('resize', measure);
    const ro = new ResizeObserver(measure);
    for (const el of document.querySelectorAll('.topbar, .loading, .counts, .side, .library, .phone-edit .tool')) ro.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      removeEventListener('resize', measure);
      ro.disconnect();
    };
  }, [clean, tool, loading, library, selected, set]);
}

export interface HallScreenProps {
  hall: HallResponse | null;
  mode: 'edit' | 'view';
  onRetry: () => void;
  renew?: () => Promise<FileAccess>;
  /** The top bar's start: the layout's name, the way back, the save state. */
  head: ReactNode;
  /** Buttons for this layout on a laptop's top bar (history, sharing). */
  actions?: ReactNode;
  /** The same, as menu entries in the phone's settings. */
  phoneActions?: { label: string; run: () => void }[];
  children?: ReactNode;
}

export function HallScreen({ hall, mode, onRetry, renew, head, actions, phoneActions, children }: HallScreenProps) {
  const [busy, setBusy] = useState(false);
  const clean = useVenue((s) => s.clean);
  const set = useVenue((s) => s.set);
  const editable = useVenue((s) => s.editable);
  const busyRef = useRef(false);

  useInsets(clean);

  useEffect(() => {
    // what a previous visit to the hall left behind
    set({ clean: false, tool: 'none', library: false, menuOpen: false, pendingTape: null, measurements: [] });
  }, [set]);

  const changeView = useCallback(
    (v: ViewMode) => {
      set({ view: v, pendingTape: null });
      rememberView(v);
    },
    [set],
  );

  const shoot = useCallback(async () => {
    const v = viewer();
    if (!v || busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try {
      const blob = await v.screenshot(!venue().clean);
      const name = `${venue().manifest?.hall.name ?? 'Hall'} ${stamp()}.png`;
      const file = new File([blob], name, { type: 'image/png' });
      const touch = matchMedia('(pointer: coarse)').matches;
      if (touch && navigator.canShare?.({ files: [file] })) {
        // Sharing needs a fresh tap, so the picture waits behind a button (it can go straight to Photos).
        venue().toast('Screenshot ready.', {
          action: { label: 'Save', run: () => void navigator.share({ files: [file], title: name }).catch(() => {}) },
          ms: 15000,
        });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = name;
        document.body.append(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 30000);
        venue().toast('Screenshot saved to your downloads.');
      }
    } catch {
      venue().toast('The screenshot couldn’t be made. Try again.', { tone: 'bad' });
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }, []);

  const reset = useCallback(() => {
    viewer()?.resetView();
    set({ menuOpen: false });
  }, [set]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      if (document.querySelector('.dialog-backdrop')) return;
      const l = layoutState();
      const chosen = new Set(l.selection);
      const canEdit = mode === 'edit' && venue().editable;
      if ((e.ctrlKey || e.metaKey) && !e.altKey) {
        const k = e.key.toLowerCase();
        if (k === 's' && mode === 'edit') {
          e.preventDefault();
          return void saving().save();
        }
        if (!canEdit) return;
        if (k === 'z' || k === 'y' || (k === 'd' && chosen.size)) e.preventDefault();
        if (k === 'z') return e.shiftKey ? l.redo() : l.undo();
        if (k === 'y') return l.redo();
        if (k === 'd' && chosen.size) {
          const r = duplicate(l.layout, chosen, 0.45, -0.45);
          l.commit(r.layout);
          return l.select(r.ids);
        }
        return;
      }
      if (canEdit && e.altKey && !e.ctrlKey && !e.metaKey && e.code === 'KeyR' && chosen.size) {
        e.preventDefault();
        return l.commit(rotateBy(l.layout, chosen, ((e.shiftKey ? 1 : -1) * Math.PI) / 180));
      }
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const s = venue();
      if (canEdit && chosen.size) {
        switch (e.key.toLowerCase()) {
          case 'delete':
          case 'backspace':
            e.preventDefault();
            return l.commit(remove(l.layout, chosen));
          case 'r':
            return l.commit(rotateBy(l.layout, chosen, e.shiftKey ? STEP : -STEP));
          case 'l':
            return l.commit(setLocked(l.layout, chosen, !l.layout.placements.filter((p) => chosen.has(p.id)).every((p) => p.locked)));
        }
      }
      switch (e.key.toLowerCase()) {
        case '1':
          return changeView('plan');
        case '2':
          return changeView('orbit');
        case '3':
          return changeView('walk');
        case 't':
          return s.set({ tool: s.tool === 'tape' ? 'none' : 'tape', pendingTape: null });
        case 'c':
          return s.set({ clean: !s.clean, menuOpen: false });
        case 'p':
          return void shoot();
        case '0':
        case 'home':
          return reset();
        case 'escape':
          if (s.menuOpen) return s.set({ menuOpen: false });
          if (s.pendingTape) return s.set({ pendingTape: null });
          if (s.tool !== 'none') return s.set({ tool: 'none' });
          if (l.placing) return l.setPlacing(null);
          if (s.library) return s.set({ library: false });
          if (l.selection.length) return l.select([]);
          if (s.clean) return s.set({ clean: false });
      }
    };
    addEventListener('keydown', onKey);
    return () => removeEventListener('keydown', onKey);
  }, [changeView, shoot, reset, mode]);

  const editing = mode === 'edit' && editable;
  return (
    <>
      <Stage hall={hall} renew={renew} />
      <PlanPreview />
      {!clean && (
        <>
          <TopBar onView={changeView} onScreenshot={() => void shoot()} busy={busy} head={head} actions={actions} editing={editing} />
          <Loading onRetry={onRetry} />
          {editing && <Library />}
          {editing && <PlacingHint />}
          {editing && <Inspector />}
          <TapePanel />
          <Settings onReset={reset} layoutActions={phoneActions} />
          <Counts />
          <Diagnostics />
          <WalkHelp />
          {children}
        </>
      )}
      {clean && <CleanControls onScreenshot={() => void shoot()} busy={busy} />}
    </>
  );
}
